import { Download } from 'lucide-react'

export interface ColumnDef<T = Record<string, unknown>> {
  /** Header label shown in the CSV */
  header: string
  /** Key of the data object, or a function that returns the cell value */
  accessor: keyof T | ((row: T) => string | number | null | undefined)
}

interface ExportCSVProps<T = Record<string, unknown>> {
  data: T[]
  columns: ColumnDef<T>[]
  filename?: string
  label?: string
  disabled?: boolean
}

function cellValue<T>(row: T, col: ColumnDef<T>): string {
  const raw =
    typeof col.accessor === 'function'
      ? col.accessor(row)
      : (row as Record<string, unknown>)[col.accessor as string]

  if (raw === null || raw === undefined) return ''
  const s = String(raw)
  // Wrap in quotes if the value contains a comma, newline, or quote
  if (s.includes(',') || s.includes('\n') || s.includes('"')) {
    return `"${s.replace(/"/g, '""')}"`
  }
  return s
}

function buildCSV<T>(data: T[], columns: ColumnDef<T>[]): string {
  const header = columns.map(c => c.header).join(',')
  const rows = data.map(row => columns.map(col => cellValue(row, col)).join(','))
  return [header, ...rows].join('\r\n')
}

function downloadCSV(csv: string, filename: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename.endsWith('.csv') ? filename : `${filename}.csv`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export default function ExportCSV<T = Record<string, unknown>>({
  data,
  columns,
  filename = 'export',
  label = 'Export CSV',
  disabled = false,
}: ExportCSVProps<T>) {
  const handleClick = () => {
    if (!data.length) return
    const csv = buildCSV(data, columns)
    downloadCSV(csv, filename)
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || data.length === 0}
      className="btn-secondary gap-1.5 text-xs"
      title={data.length === 0 ? 'No data to export' : `Download ${filename}.csv`}
    >
      <Download size={13} aria-hidden />
      {label}
    </button>
  )
}
